import {EditorBlock} from '@seed-hypermedia/client/editor-types'
import {editorBlocksToHMBlockNodes} from '@seed-hypermedia/client/editorblock-to-hmblock'
import {hmBlocksToEditorContent} from '@seed-hypermedia/client/hmblock-to-editorblock'
import isEqual from 'lodash/isEqual'
import {useMemo} from 'react'
import {compareBlocksWithMap, createBlocksMap, extractDeletes} from '../utils/document-changes'
import {getNavigationChanges} from '../utils/navigation-changes'
import {useEditorHandlersRef} from './editor-handlers-context'
import {
  selectBindingSchemaDrafts,
  selectBlocks,
  selectDocument,
  selectIsEditing,
  selectMetadata,
  selectNavigation,
  selectSaveStatus,
  selectSchemaDraft,
  useDocumentSelector,
} from './use-document-machine'

/**
 * A paragraph counts as "empty" when it has no inline content at all, or its
 * inline content is only empty text nodes. The live editor represents an empty
 * trailing paragraph as `content: []`, but the same block round-tripped through
 * `hmBlocksToEditorContent` (i.e. loaded back from a published document) comes
 * back as `content: [{type: 'text', text: '', ...}]`. Both must be treated as
 * empty, otherwise the two representations diff against each other and produce a
 * phantom "delete" that never clears — leaving the Publish button green forever
 * after a publish.
 */
function isEmptyEditorContent(content: EditorBlock['content']): boolean {
  if (!content || (Array.isArray(content) && content.length === 0)) return true
  if (!Array.isArray(content)) return false
  return content.every((item: any) => item?.type === 'text' && (item.text ?? '') === '')
}

function removeTrailingEmptyParagraphs(blocks: EditorBlock[]): EditorBlock[] {
  const trimmed = [...blocks]
  while (true) {
    const lastBlock = trimmed[trimmed.length - 1]
    if (!lastBlock) break
    if (lastBlock.type !== 'paragraph') break
    if (lastBlock.children.length !== 0) break
    if (!isEmptyEditorContent(lastBlock.content)) break
    trimmed.pop()
  }
  return trimmed
}

/**
 * Count of unpublished changes: block diffs against the published document plus
 * any metadata fields touched this session (title, summary, etc.). Reads
 * `editor.topLevelBlocks` via `EditorHandlersContext` and re-runs whenever
 * `selectSaveStatus` ticks.
 *
 * Lifted from `frontend/apps/desktop/src/components/editing-toolbar.tsx` so
 * the web-side editing toolbar can reuse the same diff logic.
 */
export function useUnpublishedChangeCount(): number {
  const handlersRef = useEditorHandlersRef()
  const isEditing = useDocumentSelector(selectIsEditing)
  const metadata = useDocumentSelector(selectMetadata)
  const navigation = useDocumentSelector(selectNavigation)
  const publishedDoc = useDocumentSelector(selectDocument)
  const blocks = useDocumentSelector(selectBlocks)
  const saveStatus = useDocumentSelector(selectSaveStatus)
  const schemaDraft = useDocumentSelector(selectSchemaDraft)
  const bindingSchemaDrafts = useDocumentSelector(selectBindingSchemaDrafts)

  return useMemo(() => {
    const publishedMetadata = publishedDoc?.version ? publishedDoc.metadata ?? {} : {}
    const metadataChangeCount = Object.entries(metadata ?? {}).filter(
      ([key, value]) => !isEqual(value, publishedMetadata[key]),
    ).length
    // A drafted schema is a change of its own: the working schema behind `schemaDefinition`, and
    // each binding schema (`attributesSchema` / `childAttributesSchema`) authored on the Attributes
    // tab. None of them touches the metadata until publish freezes them into objects.
    const schemaChangeCount = (schemaDraft ? 1 : 0) + Object.keys(bindingSchemaDrafts ?? {}).length
    // Site-header nav edits don't touch the editor or metadata, so count
    // them via the same diff used at publish time. `navigation === undefined`
    // means no nav edits this session — return 0 ops.
    const navigationChangeCount = getNavigationChanges(navigation, publishedDoc?.detachedBlocks?.navigation).length
    // The published document is the baseline throughout the draft's lifecycle.
    // An editor snapshot can contain draft content even after that draft is discarded.
    const diffBaseline = removeTrailingEmptyParagraphs(
      hmBlocksToEditorContent(publishedDoc?.content ?? [], {childrenType: 'Group'}),
    )
    const baselineMap = createBlocksMap(editorBlocksToHMBlockNodes(diffBaseline), '')
    const countBlockChanges = (editorBlocks: EditorBlock[]) => {
      const current = removeTrailingEmptyParagraphs(editorBlocks)
      if (!diffBaseline.length) return current.length
      const {changes, touchedBlocks} = compareBlocksWithMap(baselineMap, current, '')
      return changes.length + extractDeletes(baselineMap, touchedBlocks).length
    }
    const firstMachineBlock = blocks[0] as any
    const machineEditorBlocks =
      firstMachineBlock && !('block' in firstMachineBlock)
        ? (blocks as unknown as EditorBlock[])
        : hmBlocksToEditorContent(blocks)
    const machineBlockChanges = countBlockChanges(machineEditorBlocks)
    // Read-only editors can still hold their previous/placeholder content while
    // React mounts the published document. That snapshot is not an authored edit.
    const editorBlockChanges =
      isEditing && handlersRef.current?.getCurrentBlocks ? countBlockChanges(handlersRef.current.getCurrentBlocks()) : 0
    return (
      Math.max(machineBlockChanges, editorBlockChanges) +
      metadataChangeCount +
      navigationChangeCount +
      schemaChangeCount
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, metadata, navigation, publishedDoc, blocks, saveStatus, handlersRef, schemaDraft, bindingSchemaDrafts])
}
